import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Message, ConfirmationService } from 'primeng/components/common/api';
import { saveAs } from 'file-saver';

import { EvaluationSystemService } from '../../services/evaluation-system.service';
import { CurrentUserService } from '../../services/current-user.service';
import { RdpService } from '../../services/rdp.service';
import { environment } from '../../../environments/environment';
import { ComputerStates } from '../../models/computerState';
import { EvaluationStates } from '../../models/evaluationState';
import { Computer, Evaluation, EvaluationService, StartEvaluationParameters } from '../../services/GI.Web.Services.g';
import { ErrorHandling } from '../../ErrorHandling';

@Component({
  selector: 'app-userConnect',
  templateUrl: './userConnect.component.html',
  styleUrls: ['./userConnect.component.css']
})
export class UserConnectComponent implements OnInit {
  constructor(
    private _evaluationService: EvaluationService,
    private _evaluationSystemService: EvaluationSystemService,
    private _confirmationService: ConfirmationService,
    private _currentUserService: CurrentUserService,
    private _rdpService: RdpService) { }

  public evaluation: Evaluation = null;
  public clientComputer: Computer = null;
  public userConnectComputerStates: any = ComputerStates;
  public stateMessages: Message[] = [];
  public errorMessages: Message[] = [];
  public showStartEvalDialog = false;
  public startingEval = false;
  public userName: string = null;
  public password: string = null;
  public passwordVerification: string = null;
  public revealed = false;

  private currentUser: string;

  ngOnInit() {
    this.getUser().subscribe(_ => {
      this.getEvaluation();
    });
  }

  getUser(): Observable<string> {
    return this._currentUserService.getUser().map(user => {
      this.currentUser = user.email;
      return this.currentUser;
    });
  }

  async getEvaluation() {
    try {
      this.evaluation = await this._evaluationSystemService.getUserEvaluation().toPromise();
      if (this.evaluation.state === EvaluationStates.deploying) {
        this.updateMessageBannerForDeployment(EvaluationStates.deploying);
      }
      this.initializeClientComputer();
      // Always refresh.
      this.refreshEvaluation();
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error);
    }
  }

  startEvaluation() {
    this.showStartEvalDialog = true;
  }

  async saveStartEvalParameters() {
    this.showStartEvalDialog = false;
    this.startingEval = true;
    try {
      await this._evaluationService.startEvaluation(this.evaluation.name,
        new StartEvaluationParameters({ userName: this.evaluation.vmUserName, password: this.evaluation.vmPassword })).toPromise();
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error);
    }
  }

  refreshEvaluation() {
    this._evaluationSystemService.getEvaluation(this.evaluation.name).subscribe(result => {
      if (result.state !== this.evaluation.state) {
        this.updateMessageBannerForDeployment(result.state);
      }
      this.startingEval = false;  // unclear disable of button while waiting for 1rst refresh
      this.evaluation = result;

      // Update Client computer state
      const userClient = this.evaluation.clients.find(c => this.safeLower(c.user) === this.safeLower(this.currentUser));
      if (this.clientComputer.resourceId === 'not ready') {
        this.initializeClientComputer();
      }
      else {
        this.updateClientState(userClient);
      }
      setTimeout(() => this.refreshEvaluation(), environment.pollTime);
    });
  }

  initializeClientComputer() {
    if (this.evaluation.clients.length > 0) {
      const userClient = this.evaluation.clients.find(c => this.safeLower(c.user) === this.safeLower(this.currentUser));
      if (userClient != null) {
        this.clientComputer = userClient;
        if ((this.clientComputer.state === ComputerStates.starting) || (this.clientComputer.state === ComputerStates.stopping)) {
          this.updateMessageBannerForClientState(this.clientComputer.state);
        }
      } else {
        throw new Error('Evaluation did not contain computer for:' + this.currentUser);
      }
    }
    else {
      this.clientComputer = new Computer({
        resourceId: 'not ready',
        state: 'unknown',
        isServer: false
      });
    }
  }

  safeLower(text: string) {
    if (text == null) {
      return null;
    }
    return text.toLocaleLowerCase();
  }
  updateClientState(updatedClient: Computer) {
    const newState = updatedClient.state;
    // Since view is ahead of server response only update under the following conditions
    if (newState === ComputerStates.starting && (this.clientComputer.state === ComputerStates.stopped || this.clientComputer.state === ComputerStates.unknown)) {
      this.clientComputer.state = ComputerStates.starting;
      this.updateMessageBannerForClientState(ComputerStates.starting);
    }
    else if (newState === ComputerStates.running && (this.clientComputer.state === ComputerStates.starting || this.clientComputer.state === ComputerStates.unknown)) {
      this.clientComputer.state = ComputerStates.running;
      // Also update IP Address on running instances
      this.clientComputer.ipAddress = updatedClient.ipAddress;
      this.updateMessageBannerForClientState(ComputerStates.running);
    }
    else if (newState === ComputerStates.stopping && (this.clientComputer.state === ComputerStates.running || this.clientComputer.state === ComputerStates.unknown)) {
      this.clientComputer.state = ComputerStates.stopping;
      this.updateMessageBannerForClientState(ComputerStates.stopping);
    }
    else if (newState === ComputerStates.stopped && (this.clientComputer.state === ComputerStates.stopping || this.clientComputer.state === ComputerStates.unknown)) {
      this.clientComputer.state = ComputerStates.stopped;
      this.updateMessageBannerForClientState(ComputerStates.stopped);
    }
  }

  cancelStartEvalParameters() {
    this.userName = null;
    this.password = null;
    this.showStartEvalDialog = false;
  }

  confirmStopEvaluation() {
    this._confirmationService.confirm({
      message: 'Are you sure you want to STOP ' + this.evaluation.displayName + '?',
      accept: () => {
        this.stopEvaluation();
      }
    });
  }

  async stopEvaluation() {
    try {
      await this._evaluationService.stopEvaluation(this.evaluation.name).toPromise();
    } catch (error) {
      ErrorHandling.getErrorMessages(error);
    }
  }

  async startInstance() {
    const startingClient = new Computer({
      resourceId: this.clientComputer.resourceId,
      state: ComputerStates.starting,
      ipAddress: this.clientComputer.ipAddress,
      isServer: false
    });
    this.updateClientState(startingClient);
    try {
      await this._evaluationService.startClient(this.evaluation.name, this.currentUser).toPromise();
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error);
    }
  }

  async stopInstance() {
    const stoppingClient = new Computer({
      resourceId: this.clientComputer.resourceId,
      state: ComputerStates.stopping,
      ipAddress: this.clientComputer.ipAddress,
      isServer: false
    });
    this.updateClientState(stoppingClient);
    try {
      await this._evaluationService.stopClient(this.evaluation.name, this.currentUser).toPromise();
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error);
    }
  }

  connect() {
    const targetComputer = this.evaluation.clients.find(c => c.user === this.currentUser);
    const rdpString = this._rdpService.buildRdpFileAsString(targetComputer, this.evaluation.vmUserName);
    const blob = new Blob([rdpString], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, this.evaluation.name + '.rdp');
  }

  updateMessageBannerForDeployment(state: string) {
    this.stateMessages = [];
    switch (state) {
      case EvaluationStates.notStarted: {
        this.stateMessages.push({ severity: 'warn', summary: 'Not Started', detail: 'The Evaluation has not yet been initialized to start.' });
        break;
      }
      case EvaluationStates.deploying: {
        this.stateMessages.push({ severity: 'warn', summary: 'Deploying', detail: 'The Evaluation is currently deploying.' });
        break;
      }
      case EvaluationStates.started: {
        this.stateMessages.push({ severity: 'success', summary: 'Successfully Started', detail: 'The Evaluation has been deployed and started.' });
        break;
      }
      case EvaluationStates.ended: {
        this.stateMessages.push({ severity: 'success', summary: 'Successfully Ended', detail: 'The Evaluation has deallocated and successfully ended.' });
        break;
      }
      case EvaluationStates.deployFailed: {
        this.stateMessages.push({ severity: 'error', summary: 'Deployment Failed', detail: 'Something went wrong with the deployment.  Contact the Geophysical Insights team.' });
        break;
      }
      default: {
        break;
      }
    }
  }

  updateMessageBannerForClientState(state: string) {
    this.stateMessages = [];
    switch (state) {
      case ComputerStates.stopping: {
        this.stateMessages.push({ severity: 'warn', summary: 'Deallocating', detail: 'The Paradise Client virtual machine is stopping.' });
        break;
      }
      case ComputerStates.stopped: {
        this.stateMessages.push({ severity: 'success', summary: 'Deallocated', detail: 'The Paradise Client virtual machine has stopped.' });
        break;
      }
      case ComputerStates.running: {
        this.stateMessages.push({ severity: 'success', summary: 'Running', detail: 'The Paradise Client virtual machine is running and ready.' });
        break;
      }
      case ComputerStates.starting: {
        this.stateMessages.push({ severity: 'warn', summary: 'Starting', detail: 'The Paradise Client virtual machine is starting up.' });
        break;
      }
      // No status updates for Stopping, Stopped, and unknown at this point.
      default: {
        break;
      }
    }
  }

  reveal() {
    this.revealed = !this.revealed;
  }
}



// WEBPACK FOOTER //
// ./src/app/components/userConnect/userConnect.component.ts